import { useMemo, useCallback, useState } from 'react'
import { SummaryMarkdown, SummaryUpgradeNotice } from './summary'
import { useEnrichedBilling } from '@renderer/chat/hooks'
import { MeetingTranscript } from './transcript'
import { mapTranscriptEntriesToMessages } from '@renderer/utils/audio-note-helpers'
import { EmptyState, EmptyStateDescription, EmptyStateIcon, TranscriptEmptyState } from './empty-state'
import { useGlobalState } from '@highlight/ipc/react'
import { AudioNoteRenameProvider } from './types'
import type { TranscriptEntry } from '@highlight/common'
import AudioNoteIpcRenderer from '@renderer/ipc/AudioNoteIpc.renderer'
import { useUser } from '@renderer/hooks/useUser'
import { getUserFullName } from '@renderer/utils/user-name'
import { FileText } from 'lucide-react'

interface SavedMeetingNoteTranscriptProps {
  audioNote: { id?: string; transcriptEntries?: unknown }
  /** Virtualize against `scrollElement`, which the caller owns; without one this stays a plain list. */
  virtualize?: boolean
  /** The owning scroller. `null` until the caller's callback ref attaches. */
  scrollElement?: HTMLDivElement | null
}

export function SavedMeetingNoteTranscript({ audioNote, virtualize, scrollElement }: SavedMeetingNoteTranscriptProps) {
  const initialEntries = useMemo(
    () => (Array.isArray(audioNote.transcriptEntries) ? audioNote.transcriptEntries : []),
    [audioNote.transcriptEntries],
  )
  const [localEntries, setLocalEntries] = useState<Array<TranscriptEntry> | null>(null)
  const transcriptEntries = (localEntries ?? initialEntries) as Array<TranscriptEntry>
  const [manualOverrides] = useGlobalState<Record<string, string>>('speaker-manual-overrides', {})
  const hasOverrides = Object.keys(manualOverrides).length > 0
  const ownerName = getUserFullName(useUser())
  const messages = useMemo(
    () => mapTranscriptEntriesToMessages(transcriptEntries, hasOverrides ? manualOverrides : undefined, ownerName),
    [hasOverrides, manualOverrides, ownerName, transcriptEntries],
  )

  const handleEntriesChanged = useCallback(
    (entries: Array<TranscriptEntry>) => {
      console.log('[SavedMeetingNote] Entries changed, count:', entries.length, 'noteId:', audioNote?.id)
      setLocalEntries(entries)
      if (audioNote?.id) {
        AudioNoteIpcRenderer.updateAudioNote({ id: audioNote.id, updates: { transcriptEntries: entries } })
          .then(() => console.log('[SavedMeetingNote] Backend update succeeded'))
          .catch((err) => console.error('[SavedMeetingNote] Failed to persist:', err))
      }
    },
    [audioNote?.id],
  )

  const renameContext = useMemo(
    () =>
      audioNote?.id ? { audioNoteId: audioNote.id, transcriptEntries, onEntriesChanged: handleEntriesChanged } : null,
    [audioNote?.id, transcriptEntries, handleEntriesChanged],
  )

  return (
    <AudioNoteRenameProvider value={renameContext}>
      <MeetingTranscript
        messages={messages}
        virtualize={virtualize}
        scrollElement={scrollElement}
        // Only give up our own scrolling when someone else's scroller owns it.
        className={virtualize ? 'overflow-visible' : undefined}
        emptyState={<TranscriptEmptyState />}
      />
    </AudioNoteRenameProvider>
  )
}

export function SavedMeetingNoteSummary({ summary }: { summary?: string | null }) {
  const { isFree } = useEnrichedBilling()

  if (!summary) {
    // Summaries are only generated server-side for Pro users — tell free users
    // why the summary is missing instead of showing a dead-end empty state.
    if (isFree) {
      return <SummaryUpgradeNotice context="saved-meeting-note-summary" />
    }
    return (
      <div className="flex flex-1 flex-col items-center justify-center">
        <EmptyState>
          <EmptyStateIcon>
            <FileText />
          </EmptyStateIcon>
          <EmptyStateDescription>Summary not available</EmptyStateDescription>
        </EmptyState>
      </div>
    )
  }

  return <SummaryMarkdown summary={summary} isPending={false} />
}
